import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../../security-toolbox-ui/StbModal';
import { useExtendDcloudSession, useUpdateDcloudSession } from '../../../hooks/dcloud-hooks';
import {
  DcloudEditSessionRequest,
  DcloudSession,
  formatDcloudDate,
  formatDcloudDateForWire,
  getDcloudSessionId,
  toDateTimeLocalValue,
} from '../../../support/dcloudService';

type DcloudEditSessionModalProps = {
  open: boolean;
  dataCenter: string;
  session: DcloudSession | null;
  onClose: () => void;
  onSaved?: () => void;
};

const EXTEND_PRESETS = [30, 60, 120];

const DcloudEditSessionModal: React.FC<DcloudEditSessionModalProps> = ({
  open,
  dataCenter,
  session,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [startValue, setStartValue] = useState('');
  const [stopValue, setStopValue] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const update = useUpdateDcloudSession(dataCenter);
  const extend = useExtendDcloudSession(dataCenter);

  useEffect(() => {
    if (!open || !session) return;

    setName(session.name || '');
    setStartValue(toDateTimeLocalValue(session.start));
    setStopValue(toDateTimeLocalValue(session.stop));
    setFormError(null);
    update.reset();
    extend.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id]);

  const sessionId = session ? session.id ?? getDcloudSessionId(session.raw) : null;

  const handleSubmit = async () => {
    if (!session || sessionId == null) {
      setFormError('This session has no id, so dCloud cannot update it.');
      return;
    }

    const start = startValue ? new Date(startValue) : null;
    const stop = stopValue ? new Date(stopValue) : null;

    if (start && Number.isNaN(start.getTime())) {
      setFormError('Provide a valid start time.');
      return;
    }

    if (stop && Number.isNaN(stop.getTime())) {
      setFormError('Provide a valid end time.');
      return;
    }

    if (start && stop && stop <= start) {
      setFormError('The end time must be after the start time.');
      return;
    }

    setFormError(null);

    // Only send what changed. dCloud treats an EditSessionRequest as a full
    // replacement of the fields it receives, so echoing unchanged values back
    // is how sessions accidentally get re-timed.
    const payload: DcloudEditSessionRequest = {};

    if (name && name !== session.name) payload.name = name;

    if (start && toDateTimeLocalValue(session.start) !== startValue) {
      payload.start = formatDcloudDateForWire(start);
    }

    if (stop && toDateTimeLocalValue(session.stop) !== stopValue) {
      payload.stop = formatDcloudDateForWire(stop);
    }

    if (!Object.keys(payload).length) {
      onClose();
      return;
    }

    try {
      await update.mutateAsync({ sessionId, payload });
    } catch {
      // Surfaced through update.error below.
      return;
    }

    onSaved?.();
    onClose();
  };

  const handleExtend = async (minutes: number) => {
    if (!session) return;

    setFormError(null);

    try {
      await extend.mutateAsync({ session, extraMinutes: minutes });
    } catch {
      return;
    }

    onSaved?.();
    onClose();
  };

  const submitError =
    formError ||
    (update.isError ? update.error?.message : null) ||
    (extend.isError ? extend.error?.message : null);

  return (
    <StbModal
      open={open}
      role="update"
      title="Edit dCloud session"
      scrollable
      submitLabel="Save changes"
      submitDisabled={!session || sessionId == null}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? (
          <p className="m-0 text-sm text-amber-800">{submitError}</p>
        ) : undefined
      }
    >
      {!session ? (
        <p className="m-0 text-sm text-gray-600">No session selected.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="text-sm text-gray-600">
            <div>{session.demoName || session.name}</div>
            <div className="text-xs text-gray-500">
              {`${dataCenter.toUpperCase()} • currently ${formatDcloudDate(
                session.start,
              )} to ${formatDcloudDate(session.stop)}`}
            </div>
          </div>

          <TextField
            label="Session name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label="Start"
              type="datetime-local"
              value={startValue}
              onChange={(e) => setStartValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={
                session.state === 'active'
                  ? 'A running session cannot be moved earlier than it started.'
                  : undefined
              }
              fullWidth
            />

            <TextField
              label="End"
              type="datetime-local"
              value={stopValue}
              onChange={(e) => setStopValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          {session.canRequestExtension && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-600">Quick extend:</span>
              {EXTEND_PRESETS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  className="rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
                  disabled={extend.isPending || update.isPending || !session.stop}
                  onClick={() => handleExtend(minutes)}
                >
                  {minutes < 60 ? `+${minutes} min` : `+${minutes / 60} hr`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </StbModal>
  );
};

export default DcloudEditSessionModal;
